import React, { useState } from "react";

import { useNavigate } from "react-router-dom";

import Typography from "@mui/material/Typography";

import useIntegration from "@app/analytics/hooks/integrations/useIntegration";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import IntegrationDetailHeader from "@app/analytics/Integrations/components/IntegrationDetailHeader";
import IntegrationError from "@app/analytics/Integrations/components/IntegrationError";
import IntegrationNotConnected from "@app/analytics/Integrations/components/IntegrationNotConnected";
import TopicReportsAlert from "@app/analytics/Integrations/components/TopicReportsAlert";
import ConnectTeamsChannelsModal from "@app/analytics/Integrations/ConnectTeamsChannelsModal";
import { PROVIDER_CONTENT } from "@app/analytics/Integrations/constants";
import useConnectIntegrationToasts from "@app/analytics/Integrations/hooks/useConnectIntegrationToasts";
import {
  IntegrationDetailContent,
  LoadingIntegrationDetail,
} from "@app/analytics/Integrations/styles";
import { PRODUCTIVITY_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { toast } from "@app/common/hooks/useToasts";
import LINKS from "@app/common/links";
import type { ProductivityProviderId } from "@app/common/types/providerTypes";
import { withQueryParams } from "@app/common/utils";

import InfoPanel from "@rui/InfoPanel";
import Link from "@rui/Link";
import { Column } from "@rui/Stack";
import { Bold } from "@rui/styles";

const GmailPrivacyContent = () => (
  <>
    {" "}
    Read's use of information received from Google adheres to the{" "}
    <Link
      href={LINKS.THIRD_PARTY.GOOGLE_API_SERVICES_DATA_POLICY}
      underline
      fontWeight={400}
    >
      Google API Services User Data Policy
    </Link>
    , including the Limited Use requirements.
  </>
);

const TeamsLimitedData = () => {
  const [open, setOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setOpen(true);
  };

  return (
    <>
      <InfoPanel severity="warning" title="Limited Topic Data">
        Currently, your Teams topic summaries are limited to{" "}
        <Bold>chat messages only</Bold>. To generate more robust topic summaries
        that include channels, your Teams admin needs to grant Read additional
        permissions. <Link onClick={handleClick}>Connect Teams channels</Link>
      </InfoPanel>
      <ConnectTeamsChannelsModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};

const TeamsActionRequired = ({ onContinue }: { onContinue: () => void }) => {
  const handleContinue = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onContinue();
  };

  return (
    <InfoPanel severity="warning" title="Action Needed">
      You should now have permission to connect Read with Teams channels for
      more robust topic summaries.{" "}
      <Link onClick={handleContinue}>Continue with connection</Link>
    </InfoPanel>
  );
};

const ProductivityIntegrationConfig = ({
  providerId,
}: {
  providerId: ProductivityProviderId;
}) => {
  const { getEnableUrl, getIntegration, isLoading } = useIntegrations();

  const navigate = useNavigate();

  const providerConfig = PRODUCTIVITY_PROVIDERS_BY_ID[providerId];
  const providerContent = PROVIDER_CONTENT[providerId];

  const integration = getIntegration({ providerId, scope: "user" });

  const { authorize, disconnect, enable } = useIntegration();

  useConnectIntegrationToasts({
    integration,
    targetAction: providerConfig.connectAction,
  });

  if (isLoading) {
    return <LoadingIntegrationDetail />;
  }

  if (!integration) {
    return <IntegrationError />;
  }

  const handleAuthorize = ({
    connectParams,
  }: {
    connectParams?: { [key: string]: string };
  } = {}) => {
    return authorize.mutate({
      providerId,
      returnRoute: integration.route,
      scope: "user",
      connectParams,
    });
  };

  const handleClickConnect = () => {
    const enableUrl = getEnableUrl({ providerId });
    if (enableUrl) {
      return enable.mutate(enableUrl, {
        onSuccess: () => {
          // simulate the connection success query params to trigger the toast
          navigate(
            withQueryParams(location.pathname, {
              action: providerConfig.connectAction,
              success: true,
            })
          );
        },
        onError: () => handleAuthorize(),
      });
    }

    return handleAuthorize();
  };

  const handleDisconnect = () => {
    const displayName = integration.displayNameShort;

    disconnect.mutate(providerId, {
      onError: () => {
        toast.error(
          `There was an error disconnecting ${displayName}. Please try again or log into your ${displayName} account to disconnect Read.`
        );
      },
    });
  };

  const connectCta = {
    onClick: handleClickConnect,
    isLoading: enable.isPending || enable.isError || authorize.isPending,
  };

  const teamsHasLimitedData =
    providerId === "microsoft_graph_teams_chat_ingest" &&
    integration.limitedData;

  return (
    <>
      <IntegrationDetailHeader
        cta={connectCta}
        documentationUrl={integration.documentationUrl}
        isDisconnecting={disconnect.isPending}
        onClickDisconnect={handleDisconnect}
        // reauth is handled in different alerts (below) in the limited data case
        onReauth={teamsHasLimitedData ? undefined : handleAuthorize}
        provider={integration}
      >
        <Column variant="left" spacing={2}>
          <Typography variant="bodySecondaryReg" color="shades.neutral.600">
            {providerContent.headerDescription ??
              providerContent.shortDescription}
            {providerId === "google_email" && <GmailPrivacyContent />}
          </Typography>

          {teamsHasLimitedData &&
            (integration.reauthRequired ? (
              <TeamsActionRequired
                onContinue={() =>
                  handleAuthorize({
                    connectParams: { include_admin_scopes: "true" },
                  })
                }
              />
            ) : (
              <TeamsLimitedData />
            ))}
          <TopicReportsAlert />
        </Column>
      </IntegrationDetailHeader>
      {integration.isConnected ? (
        <IntegrationDetailContent spacing={1}>
          {integration.providerEmail && (
            <Typography variant="bodyPrimaryMed" color="shades.neutral.800">
              {integration.providerEmail} is connected
            </Typography>
          )}
          <Typography variant="bodySecondaryReg" color="shades.neutral.600">
            {providerContent.configurationDescription}
          </Typography>
        </IntegrationDetailContent>
      ) : (
        <IntegrationNotConnected cta={connectCta} provider={integration} />
      )}
    </>
  );
};

export default ProductivityIntegrationConfig;
